import { redirect } from '@tanstack/react-router';

import { ROUTE_PATHS } from '@modules/core/config/modules';

import type { TAuthStatus } from '@bootstrap/auth/types.ts';

import { AuthClient } from './auth-client';

/**
 * Wait for authentication status to be resolved (not loading)
 */
async function waitForAuthResolution(client: AuthClient, maxWaitMs = 5000): Promise<void> {
  const startTime = Date.now();

  while (client.snapshot.status === 'loading' || client.snapshot.status === 'idle') {
    if (Date.now() - startTime > maxWaitMs) {
      throw new Error('Authentication status resolution timeout');
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
}

export function requireAuthGuard(client: AuthClient) {
  return async () => {
    if (client.snapshot.status === 'authenticated') {
      return null;
    }

    // Always hydrate from session when not authenticated
    // This ensures we check the SESSION_TOKEN cookie even if in-memory state is stale
    await client.hydrateFromSession();

    await waitForAuthResolution(client);

    if ((client.snapshot.status as TAuthStatus) !== 'authenticated') {
      const returnUrl = window.location.pathname + window.location.search;

      throw redirect({
        to: ROUTE_PATHS.login,
        search: { from: returnUrl },
      });
    }

    return null;
  };
}

export function requireRoleGuard(client: AuthClient, roles: string[]) {
  return async () => {
    if (client.snapshot.status !== 'authenticated') {
      // Always hydrate from session when not authenticated
      await client.hydrateFromSession();
      await waitForAuthResolution(client);
    }

    const isAuthenticated = client.snapshot.status === 'authenticated';

    if (!isAuthenticated) {
      const returnUrl = window.location.pathname + window.location.search;
      throw redirect({
        to: ROUTE_PATHS.login,
        search: { from: returnUrl },
      });
    }

    const hasRequiredRole = client.hasAnyRole(roles);

    if (!hasRequiredRole) {
      const userRoles = client.snapshot.user?.roles || [];
      console.warn(
        `Access denied: User roles [${userRoles.join(', ')}] do not match required roles [${roles.join(', ')}]`
      );

      throw redirect({
        to: ROUTE_PATHS.login,
        search: {
          error: 'insufficient_permissions',
          required: roles.join(','),
        },
      });
    }

    return null;
  };
}
